import { MinusCircleOutlined, PlusOutlined } from '@ant-design/icons'
import { Button, Form, Input, Modal, Space } from 'antd'
import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { DatePicker } from '../../../components/Form'
import { FormattedInputNumber } from '../../../components/Misc/FormattedInputNumber'
import { Company } from '../../../types/company/Company'
import { toFormDate } from '../../../config/dayjsConfig'

interface AcquisitionsModalProps {
  open: boolean
  groupId?: string
  company?: Company
  onUpdate: (value: Company) => void
  onCancel: () => void
}

const AcquisitionsModal: React.FC<AcquisitionsModalProps> = ({ open, groupId, company, onUpdate, onCancel }) => {
  const [form] = Form.useForm<Pick<Company, 'acquisitions'>>()
  const { t } = useTranslation()

  useEffect(() => {
    form.resetFields()
    // Convert ISO dates to YYYY-MM-DD format to avoid timezone issues
    const acquisitionsWithConvertedDates = company?.acquisitions?.map(acquisition => ({
      ...acquisition,
      date: toFormDate(acquisition.date)
    }))
    form.setFieldsValue({ acquisitions: acquisitionsWithConvertedDates })
  }, [company, open])

  return (
    <Modal
      destroyOnHidden
      open={open}
      width={500}
      title={t('subsidiaries:editSubsidiary')}
      okText={t('global:save')}
      cancelText={t('global:cancel')}
      maskClosable={false}
      data-cy="acquisitions-modal"
      onCancel={() => {
        form.resetFields()
        onCancel()
      }}
      onOk={async () => {
        try {
          if (!company) return
          const values = await form.validateFields()

          form.resetFields()
          const newSubsidiary = {
            ...company,
            ...values
          }
          onUpdate(newSubsidiary)
        } catch (info) {
          console.error('Validate Failed:', info)
        }
      }}
    >
      <Form form={form} name="company_acquisitions" autoComplete="off">
        <Form.Item label={t('global:company')}>{company?.name}</Form.Item>
        <Form.List name="acquisitions">
          {(fields, { add, remove }) => (
            <>
              {fields.map(field => (
                <Space key={field.key} align="baseline">
                  <Form.Item initialValue={groupId} hidden name={[field.name, 'groupId']}>
                    <Input />
                  </Form.Item>
                  <Form.Item initialValue={company?.id} hidden name={[field.name, 'subsidiaryId']}>
                    <Input />
                  </Form.Item>
                  <Form.Item
                    label={t('contractsPage:date')}
                    name={[field.name, 'date']}
                    rules={[{ required: true, message: t('global:required-field') }]}
                  >
                    <DatePicker data-cy={`acquisition-date-${field.name}`} format="DD.MM.YYYY" />
                  </Form.Item>

                  <Form.Item
                    label={t('global:share')}
                    name={[field.name, 'percentage']}
                    initialValue={0}
                    rules={[{ required: true, message: t('global:required-field') }]}
                  >
                    <FormattedInputNumber
                      data-cy={`acquisition-percentage-${field.name}`}
                      step={1}
                      precision={2}
                      max={100}
                      percentage
                      addonAfter="%"
                    />
                  </Form.Item>

                  <MinusCircleOutlined
                    data-cy={`acquisition-remove-${field.name}`}
                    onClick={() => remove(field.name)}
                  />
                </Space>
              ))}

              <Form.Item>
                <Button data-cy="acquisition-add-btn" type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                  {`${t('global:add')} ${t('global:share').toLowerCase()}`}
                </Button>
              </Form.Item>
            </>
          )}
        </Form.List>
      </Form>
    </Modal>
  )
}

export default AcquisitionsModal
